import { makeRequest } from "../../baseApi";
import {
  parseErrorDefault,
  parseResponseDefault,
  removeLangFromFolders,
} from "../../not-sorted.utils";
import {
  advSearchAutocompleteTypes,
  advSearchInfoLangTypes,
} from "../../../../main/src/utils/AdvSearchTypes";
import { searchStrToReqParams } from "../../../utils/appServer/appServer";
import { parseResponseSearchDictionaryContent } from "./utils";
import { BOOK_PERMISSION } from "../../../utils/content/content.constants";
import { advSearchTypes } from "../../../utils/search/search.constants";
import { makeParamsString } from "../../utils";

export const advSearchInfoExpireTime = 60 * 60 * 1000; // 1 hour

export const advSearchPeriodicalsInfoByPubnr = (pubnr) => {
  const url = `/search/advanced/periodicals/${pubnr}/info/`;
  return makeRequest({
    url,
    storageCacheName: url,
    storageCacheTime: advSearchInfoExpireTime,
    parseResponse: parseResponseDefault,
  });
};

export const advSearchAplInfoByPubnr = (pubnr) => {
  const url = `/search/advanced/apl/${pubnr}/info/`;
  return makeRequest({
    url,
    storageCacheName: url,
    storageCacheTime: advSearchInfoExpireTime,
    parseResponse: parseResponseDefault,
  });
};

export const advSearchAutocomplete = (extras) => {
  const type = extras["type"];
  if (!advSearchAutocompleteTypes.includes(type)) {
    return null;
  }
  if (extras["type"]) {
    delete extras["type"];
  }
  if (extras["subtype"]) {
    if (extras["subtype"] !== "all") {
      extras["type"] = extras["subtype"];
    }
    delete extras["subtype"];
  }
  const path = `/search/advanced/${type}/autocomplete`;
  const paramsString = makeParamsString(extras, true);
  const url = path + (paramsString ? `?${paramsString}` : "");
  return makeRequest({
    url,
    memoryCache: true,
    parseResponse: (response) => {
      return response.data;
    },
  });
};

export const advSearchInfo = (extras) => {
  const type = extras["type"];
  if (!advSearchTypes.includes(type)) {
    return null;
  }
  if (extras["type"]) {
    delete extras["type"];
  }

  let path = "";
  if (!advSearchInfoLangTypes.includes(type)) {
    // common case
    path = `/search/advanced/${type}/info`;
  } else {
    // info(lang) case
    if (!extras["lang"]) {
      return null;
    }
    const lang = extras["lang"];
    if (lang.length < 2) {
      return null;
    }
    path = `/search/advanced/${type}/${lang}/info`;
    delete extras["lang"];
  }
  const paramsString = makeParamsString(extras, true);

  const url = path + (paramsString ? `?${paramsString}` : "");

  return makeRequest({
    url,
    storageCacheName: url,
    storageCacheTime: advSearchInfoExpireTime,
    parseResponse: parseResponseDefault,
  });
};

export const AdvSearchNavigationRequest = (extras) => {
  const type = extras["type"];
  if (!advSearchTypes.includes(type)) {
    return null;
  }
  if (extras["type"]) {
    delete extras["type"];
  }
  if (extras["bookpage"]) {
    extras["page"] = extras["bookpage"];
    delete extras["bookpage"];
  }

  const path = `/search/advanced/${type}/navigate`;
  const paramsString = makeParamsString(extras, true);

  const url = path + (paramsString ? `?${paramsString}` : "");

  return makeRequest({
    url,
    memoryCache: true,
    parseResponse: (response) => {
      return response.data;
    },
  });
};

export const advSearch = (query, extrasOriginal, limit, offset, folders = []) => {
  const extras = { ...extrasOriginal };
  const type = extras["type"];
  const path = advSearchTypes.includes(type) ? `/search/advanced/${type}` : "/search";
  if (extras["type"]) {
    delete extras["type"];
  }
  if (extras["subtype"]) {
    extras["type"] = extras["subtype"];
    delete extras["subtype"];
  }
  if (extras["bookpage"]) {
    extras["page"] = extras["bookpage"];
    delete extras["bookpage"];
  }
  if (extras["queryType"]) {
    extras["query_type"] = extras["queryType"];
    delete extras["queryType"];
  }
  delete extras["period"];

  // datarange process
  switch (type) {
    case "bible":
      if (folders.length && !extras.lang) {
        extras["lang"] = folders[0];
      }
      break;
    case "apl":
      if (extras["start_year"]) {
        extras["start_date"] = `${extras["start_year"]}-01-01`;
        delete extras["start_year"];
      }
      if (extras["end_year"]) {
        extras["end_date"] = `${extras["end_year"]}-12-31`;
        delete extras["end_year"];
      }
      break;

    case "book":
      if (extras.daterange) {
        switch (extras.daterange) {
          case "all":
            break;
          case "lifetime":
            extras["end_date"] = "1917-12-31";
            break;
          case "compilations":
            extras["start_date"] = "1918-01-01";
            break;
        }
        delete extras["daterange"];
      }
      break;

    case "ltms":
      if (extras["number"]) {
        if (isNaN(parseInt(extras["number"]))) {
          delete extras["number"];
        }
      }

      if (extras["start_year"]) {
        extras["start_date"] = `${extras["start_year"]}-01-01`;
        delete extras["start_year"];
      }
      if (extras["end_year"]) {
        extras["end_date"] = `${extras["end_year"]}-12-31`;
        delete extras["end_year"];
      }
      if (extras["year"]) {
        const year = extras["year"];
        extras["start_date"] = `${year}-01-01`;
        extras["end_date"] = `${year}-12-31`;
        delete extras["year"];
      }

      break;
    case "periodicals":
      delete extras["bookCode"]; // is used to set the book code input value

      if (extras["year"] && extras["month"] && extras["day"]) {
        const year = extras["year"];
        const month = extras["month"];
        const day = extras["day"];
        extras["start_date"] = `${year}-${month}-${day}`;
        extras["end_date"] = `${year}-${month}-${day}`;
        delete extras["year"];
        delete extras["month"];
        delete extras["day"];
      } else if (extras["year"] && extras["month"]) {
        const year = extras["year"];
        const month = extras["month"];
        extras["start_date"] = `${year}-${month}-01`;
        extras["end_date"] = `${year}-${month}-31`;
        delete extras["year"];
        delete extras["month"];
      } else if (extras["year"]) {
        const year = extras["year"];
        extras["start_date"] = `${year}-01-01`;
        extras["end_date"] = `${year}-12-31`;
        delete extras["year"];
      } else {
        // combine start_date-end_date with daterange('lifetime', 'compilations', 'all')
        if (extras["start_year"]) {
          extras["start_date"] = `${extras["start_year"]}-01-01`;
          delete extras["start_year"];
        }
        if (extras["end_year"]) {
          extras["end_date"] = `${extras["end_year"]}-12-31`;
          delete extras["end_year"];
        }
        if (extras["daterange"]) {
          const daterange = extras["daterange"];
          switch (daterange) {
            case "lifetime":
              if (!extras["end_date"] || "1917-12-31" < extras["end_date"]) {
                extras["end_date"] = "1917-12-31";
              }
              break;
            case "compilations":
              if (!extras["start_date"] || "1918-01-01" > extras["start_date"]) {
                extras["start_date"] = "1918-01-01";
              }
              break;
          }
          delete extras["daterange"];
        }
      }
      break;
    case "dictionary": {
      if (extras["dictionarySearchType"]) {
        extras["headings_only"] = extras["dictionarySearchType"];
      }

      const foldersWithoutLang = removeLangFromFolders(folders);
      if (foldersWithoutLang.length !== 0) {
        extras["pubnr"] = [foldersWithoutLang];
      }

      delete extras["dictionarySearchType"];
      break;
    }
  }

  if (extras["collections"]) {
    let seq;
    if (typeof extras["collections"] === "string") {
      seq = decodeURIComponent(extras["collections"]).split(",");
    } else {
      seq = extras["collections"];
    }
    if (seq.length) {
      extras["folder"] = seq;
    }
    delete extras["collections"];
  }
  const paramsStr = makeParamsString(extras, true);
  const searchParams = [`query=${encodeURIComponent(query)}`, paramsStr];
  if (limit) {
    searchParams.push(`limit=${limit}`);
  }
  if (offset) {
    searchParams.push(`offset=${offset}`);
  }
  const searchStr = "?" + searchParams.join("&");
  const fullUrl = path + searchStr;

  console.groupCollapsed(`🕵️ AS ${query} [${limit}] (${extras.lang || "no lang"})`);
  try {
    console.table(searchStrToReqParams(searchStr));
  } catch (e) {
    console.log(searchStr);
  }
  console.groupEnd();

  const request = {
    url: fullUrl,
    type: "get",
    parseResponse: (response) => {
      const { total } = response.data;
      const results = response.data.results.map((item) => {
        // isForSale = isAvailable for search items,
        // because search items do not have "action_required" property if available
        const isForSale = item.action_required === BOOK_PERMISSION.purchased;
        return {
          query,
          ...item,
          isForSale,
        };
      });
      return { total, results };
    },
  };
  return makeRequest(request);
};

export const searchDictionaryAutocompleteRequest = (query) => {
  return makeRequest({
    url: `/search/advanced/dictionary/autocomplete?q=${query}`,
    memoryCache: true,
    parseResponse: (response) => {
      console.log("dictionaryAutocompete", response.data);
      return response.data.options;
    },
  });
};

export const AdvSearchNavigation = (extras) => {
  return AdvSearchNavigationRequest(extras).then((data) => {
    if (data && data.pubnr && data.para) {
      const paraId = `${data.pubnr}.${data.para}`;
      return { paraId, lang: data.lang };
    }

    return {};
  });
};

export const searchDictionaryContent = (query, dictionary) => {
  const pubnr = dictionary ? `&pubnr=${dictionary}` : "";

  return makeRequest({
    url: `/search/advanced/dictionary/content?chapter=${query}${pubnr}`,
    memoryCache: true,
    parseResponse: parseResponseSearchDictionaryContent,
    parseError: parseErrorDefault,
  });
};

export const getScripturePubnrsRequest = () => {
  const url = "/search/advanced/scripture/books";
  return makeRequest({
    url,
    storageCacheName: url,
    storageCacheTime: advSearchInfoExpireTime,
    parseResponse: parseResponseDefault,
  });
};

export const searchDictionaryRequest = (query, dictionary, headingsOnly) => {
  const pubnr = dictionary && dictionary !== "all" ? `&pubnr=${dictionary}` : "";
  return makeRequest({
    url: `/search/advanced/dictionary?headings_only=${headingsOnly}&query=${encodeURIComponent(
      query,
    )}${pubnr}`,
    memoryCache: true,
    parseResponse: (response) => {
      return response.data.results.map((item) => ({
        id: item.para_id,
        type: "dictionary",
        ...item,
        isForSale: item.action_required === BOOK_PERMISSION.purchased,
        className: "book",
      }));
    },
    parseError: parseErrorDefault,
  });
};
